import React, {
  FC,
  createContext,
  useContext,
  useReducer,
  useEffect,
} from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { TCart, TItem, TDiscount, CartAction, CartProviderProps } from '../@types/cart';

// Modified version of notrab's react-use-cart on github
// let uuidv4 = self.crypto.randomUUID();

const SET_ITEMS = 'SET_ITEMS';
const ADD_ITEM = 'ADD_ITEM';
const UPDATE_ITEM = 'UPDATE_ITEM';
const REMOVE_ITEM = 'REMOVE_ITEM';
const EMPTY_CART = 'EMPTY_CART';
const CLOSE_CART = 'CLOSE_CART';
const UPDATE_CART_ID = 'UPDATE_CART_ID';
const SET_CART_ID = 'SET_CART_ID';
const SET_ACTIVE_STEP = 'SET_ACTIVE_STEP';
const SET_DISCOUNT = 'SET_DISCOUNT';
const REMOVE_DISCOUNT = 'REMOVE_DISCOUNT';
const GENERATE_FRESH_EMPTY_CART = 'GENERATE_FRESH_EMPTY_CART';

const now = new Date().toISOString();
const isBrowser = () => typeof window !== 'undefined';

export const initialState: any = {
  items: [],
  totalItems: 0,
  totalUniqueItems: 0,
  isEmpty: true,
  started: now,
  latestUpdate: now,
  isOpen: true,
  cartTotal: 0,
  cartSubtotal: 0,
  discount: undefined,
  activeStep: 1,
};

const CartContext = createContext<CartProviderProps>(initialState);

// Instead of UUID
export const createCartIdentifier = (len = 16) =>
  [...Array(len)].map(() => (~~(Math.random() * 36)).toString(36)).join('');

export const useCart = () => useContext(CartContext);

const getDiscountCartTotal=(cartTotal:number, discount:TDiscount | undefined)=>{
  if (discount && cartTotal){
    const discountAmt = discount.discountRate * cartTotal;
    const discountedTotal = cartTotal - discountAmt
    return discountedTotal;
  } else return cartTotal;
};

function reducer(state: CartProviderProps, action: CartAction) {
  switch (action.type) {
    case SET_ITEMS:
      return generateCartState(state, action.payload);

    case ADD_ITEM: {
      const items = [...state.items, action.payload];
      return generateCartState(state, items);
    }
    case SET_DISCOUNT: {
      return {
        ...state,
        discount: action.discount,
        cartTotal: getDiscountCartTotal(state.cartTotal, action.discount),
      };
    }
    case REMOVE_DISCOUNT: {
      return {
        ...state,
        cartTotal: state.cartSubtotal,
        discount: null
      };
    }

    case UPDATE_ITEM: {
      const items = state.items.map((item: TItem) => {
        if (item.id !== action.id) return item;
        return {
          ...item,
          ...action.payload,
        };
      });
      return generateCartState(state, items);
    };

    case REMOVE_ITEM: {
      const items = state.items.filter(i => i.id !== action.id);
      return generateCartState(state, items);
    };

    case EMPTY_CART:
      return initialState;

    case GENERATE_FRESH_EMPTY_CART:
      return {
        ...initialState,
        id: createCartIdentifier(),
        activeStep:state.activeStep,
      };

    case UPDATE_CART_ID:
      return {
        ...state,
        id: createCartIdentifier(),
      };

    case SET_CART_ID:
      return {
        ...state,
        id: action.payload,
      };

    case CLOSE_CART:
      return {
        ...state,
        isOpen: false,
      };

      case SET_ACTIVE_STEP:
        return {
          ...state,
          activeStep: action.step,
        };
    default:
      throw new Error('No action specified');
  }
}

const generateCartState = (state = initialState, items: TItem[]) => {
  const totalUniqueItems = calculateUniqueItems(items);
  const isEmpty = totalUniqueItems === 0;
  const latestUpdate = now;
  const isOpen = state.isOpen;
  const activeStep = state.activeStep;
  const discount = state.discount;
  return {
    ...initialState,
    ...state,
    items: calculateItemTotals(items),
    totalItems: calculateTotalItems(items),
    totalUniqueItems,
    cartTotal: calculateCartTotal(items, discount),
    cartSubtotal: calculateCartSubtotal(items),
    // totalWeight: calculateTotalWeight(items),
    discount,
    isEmpty,
    latestUpdate,
    isOpen,
    activeStep,
  };
};
const calculateItemTotals = (items: TItem[]) =>
  items.map(item => ({
    ...item,
    itemTotal: item.price * item.quantity!,
  }));
const calculateCartTotal = (items: TItem[], discount:TDiscount | undefined) =>{
  const subtotal = items.reduce((total, item) => total + item.quantity! * item.price, 0);
  if (discount){
    const discountAmt:number = Number(discount.discountRate)*subtotal;
    
    return subtotal - discountAmt;
  }
  else return subtotal;
}
const calculateCartSubtotal = (items: TItem[]) =>
  items.reduce((total, item) => total + item.quantity! * item.price, 0);


const calculateTotalItems = (items: TItem[]) =>
  items.reduce((sum, item) => sum + item.quantity!, 0);

// const calculateTotalWeight = (items:TItem[]) =>
//   items.reduce((sum, item) => sum + item.quantity * item.weightEach, 0)


const calculateUniqueItems = (items: TItem[]) => items.length;

const CartProvider: React.FC<{
  children?: React.ReactNode;
  id?: string;
  defaultItems?: TItem[];
  onSetItems?: (items: TItem[]) => void;
  onItemAdd?: (payload: TItem) => void;
  onItemUpdate?: (payload: object) => void;
  onItemRemove?: (id: TItem['id']) => void;
  onEmptyCart?: () => void;
  onSetActiveStep?: (payload: TCart['activeStep'])=> void;
  onSetDiscount?: (payload: TDiscount)=>void;
  onRemoveDiscount?: ()=>void;
  onResetCart?: ()=>void;
  storage?: (
    key: string,
    initialValue: string,
  ) => [string, (value: Function | string) => void];
}> = ({
  children,
  id: cartId,
  defaultItems = [],
  onSetItems,
  onItemAdd,
  onItemUpdate,
  onItemRemove,
  onEmptyCart,
  onSetActiveStep,
  onSetDiscount,
  onRemoveDiscount,
  onResetCart,
  storage = useLocalStorage,
}) => {
  const id = cartId ? cartId : createCartIdentifier();

  const [savedCart, saveCart] = storage(
    cartId ? `cs-cart-${id}` : 'cs-cart',
    JSON.stringify({
      id,
      ...initialState,
      items: defaultItems,
    }),
  );

  const [state, dispatch] = useReducer(reducer, JSON.parse(savedCart));

  useEffect(() => {
    saveCart(JSON.stringify(state));
  }, [state, saveCart]);

  const setItems = (items: TItem[]) => {
    dispatch({
      type: SET_ITEMS,
      payload: items.map(item => ({
        ...item,
        quantity: item.quantity || 1,
      })),
    });

    onSetItems && onSetItems(items);
  };
  const setDiscount = (discount: TDiscount, items:TItem[]) => {
    dispatch({
      type: SET_DISCOUNT,
      discount: discount
    });
    onSetDiscount && onSetDiscount(discount);
  };
  const removeDiscount = () => {
    dispatch({
      type: REMOVE_DISCOUNT
    });
    onRemoveDiscount && onRemoveDiscount();
  };

  const addItem = (item: TItem, quantity = 1) => {
    if (quantity <= 0) return;
    if (!item.id) throw new Error('You must provide an `id` for items');

    const currentItem = state.items.find((i: TItem) => i.id === item.id);

    if (!currentItem && !item.price)
      throw new Error('You must pass a `price` for new items');

    if (!currentItem) {
      const payload = { ...item, quantity };
      dispatch({ type: ADD_ITEM, payload });
      onItemAdd && onItemAdd(payload);
      return;
    }

    const payload = { ...item, quantity: currentItem.quantity + quantity };
    dispatch({
      type: UPDATE_ITEM,
      id: item.id,
      payload,
    });

    onItemUpdate && onItemUpdate(payload);
  };

  const updateItem = (id: TItem['id'], payload: object) => {
    if (!id || !payload) {
      return;
    }
    dispatch({ type: UPDATE_ITEM, id, payload });
    onItemUpdate && onItemUpdate(payload);
  };

  const updateItemQuantity = (id: TItem['id'], quantity: number) => {
    if (quantity <= 0) {
      onItemRemove && onItemRemove(id);
      dispatch({ type: REMOVE_ITEM, id });
      return;
    }

    const currentItem = state.items.find((item: TItem) => item.id === id);
    if (!currentItem) throw new Error('No such item to update');
    const payload = { ...currentItem, quantity };
    dispatch({
      type: UPDATE_ITEM,
      id,
      payload,
    });
    onItemUpdate && onItemUpdate(payload);
  };

  // Added this to update price when the quantity is updated
  const updateItemPrice = (id: TItem['id'], price: number) => {
    const currentItem = state.items.find((item: TItem) => item.id === id);
    if (!currentItem) throw new Error('No such item to update');
    const payload = { ...currentItem, price };
    dispatch({
      type: UPDATE_ITEM,
      id,
      payload,
    });
    onItemUpdate && onItemUpdate(payload);
  };

  const removeItem = (id: TItem['id']) => {
    dispatch({ type: REMOVE_ITEM, id });
    onItemRemove && onItemRemove(id);
  };

  const updateCartId = () =>
    dispatch({
      type: UPDATE_CART_ID,
    });

  const setCartId = (id: string) =>
    dispatch({
      type: SET_CART_ID,
      payload: id,
    });

  const emptyCart = () => {
    dispatch({
      type: EMPTY_CART,
    });
    onEmptyCart && onEmptyCart();
  };

  const closeCart = () =>
    dispatch({
      type: CLOSE_CART,
    });

  const getCartSummary = (itemCount: TCart['totalItems'])=>{
    if (itemCount === 0){return 'Your cart is empty'};
    if (itemCount === 1){return 'You have one item in your cart'};
    if (itemCount > 1){return `You have ${itemCount} items in your cart`}
    };

  const getItem = (id: TItem['id']) => {
    const currentItem = state.items.find((i: TItem) => i.id === id);
    if (!currentItem) throw new Error('No such item found');
    return currentItem;
  };

  const inCart = (id: TItem['id']) =>{
    return state.items.some((i: TItem) => i.id === id);};

  const setActiveStep = (step: TCart['activeStep']) => {
      dispatch({ type: SET_ACTIVE_STEP, step });
      onSetActiveStep && onSetActiveStep(step);
    };  

    const resetCart = ()=>{
      dispatch({type: GENERATE_FRESH_EMPTY_CART});
      onResetCart && onResetCart();
    }

  useEffect(() => {
    if (state.isOpen === false) {
      isBrowser() && window.localStorage.removeItem('cs-cart');
    }
    saveCart(JSON.stringify(state));
  }, [state.isOpen]);

  return (
    <CartContext.Provider
      value={{
        ...state,
        getItem,
        inCart,
        setItems,
        addItem,
        updateItem,
        updateItemQuantity,
        updateItemPrice,
        removeItem,
        emptyCart,
        closeCart,
        setActiveStep,
        updateCartId,
        setCartId,
        setDiscount,
        removeDiscount,
        getCartSummary,
        resetCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;